import React, { useEffect, useState } from 'react';
import { Button, InlineAlert, Modal } from '@routific/hive';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { useStore } from '../../../../../store/mst';
import styled from 'styled-components';
import AddMemberForm from './AddMemberForm';
import { RoleNames } from '../../../../../store/mst/models';
import ContactSupportButton from '../../../../shared/ContactSupportButton';

interface Props {
  visible: boolean;
  onClose: () => void;
  defaultRole?: RoleNames;
}

const StyledFooter = styled.div`
  text-align: right;
  display: inline-flex;
`;

export enum InviteErrorKey {
  'ALREADY_INVITED' = 'ALREADY_INVITED',
  'INVITE_FAILED' = 'INVITE_FAILED',
}


const AddMemberModal: React.FC<Props> = ({
  visible, onClose, defaultRole,
}) => {
  const { t } = useTranslation('components');
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [errorKey, setErrorKey] = useState<InviteErrorKey | undefined>(undefined);
  const [form] = Form.useForm();
  const { team } = useStore();

  // Reset form with correct default role when modal opens
  useEffect(() => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue({ role: defaultRole ?? RoleNames.DISPATCHER });
    }
  }, [visible, defaultRole, form]);

  const getErrorMessage = (error: InviteErrorKey) => {
    const description = error === InviteErrorKey.INVITE_FAILED ? t(`companySettings:team.addMemberForm.error.${error}.description`) : undefined;
    return {
      message: t(`companySettings:team.addMemberForm.error.${error}.message`),
      description,
    };
  };

  const onCancel = () => {
    form.resetFields();
    setIsFormValid(false);
    setErrorKey(undefined);
    onClose();
  };

  const validateForm = (isValid: boolean) => {
    setErrorKey(undefined);
    setIsFormValid(isValid);
  };

  const addMember = async (values: any) => {
    try {
      await team.createUserInvite(values.email, values.role);
      onCancel();
      team.fetchTeamMembers();
    } catch (err) {
      const key = err.response?.errors[0]?.message || '';
      if (Object.keys(InviteErrorKey).includes(key)) {
        setErrorKey(key);
      } else {
        setErrorKey(InviteErrorKey.INVITE_FAILED);
      }
    }
  };

  const FooterContent: React.FC = () => (
    <StyledFooter
      data-cy="add-member-form-footer"
    >
      <Button
        onClick={onCancel} data-cy="add-member-form-cancel-btn">{t('debugOrganizationModal.cancelButton')}</Button>
      <Button
        key="contact"
        type="primary"
        onClick={() => form.submit()}
        disabled={!isFormValid}
        data-cy={'add-member-form-submit-btn'}
        data-testid={'add-member-form-submit-btn'}
      >
        {t('companySettings:team.addMemberForm.footer.addBtn')}
      </Button>
    </StyledFooter>
  );

  const getSupportButton = () => {
    return errorKey === InviteErrorKey.INVITE_FAILED ? <ContactSupportButton style={{ marginTop: 0 }} /> : <></>;
  };

  return (
    <Modal
      title={t('companySettings:team.addMemberForm.title')}
      visible={visible}
      size="small"
      centered
      destroyOnClose
      data-cy="add-member-modal"
      data-testid="add-member-modal"
      footer={<FooterContent/>}
      closable={false}
      maskClosable={false}
      zIndex={2000}
    >
      <AddMemberForm form={form} addMember={addMember} validateForm={validateForm} defaultRole={defaultRole} />
      {errorKey && (
        <InlineAlert
          type='error'
          data-cy="add-member-modal-inline-alert"
          {...getErrorMessage(errorKey)}
          content={getSupportButton()}
        />
      )}
    </Modal>
  );
};

export default observer(AddMemberModal);
